import { useRef, useState } from "react";
import { viewFromApi } from "@/components/FileViewer";
import { Paperclip, FileText, Image as ImageIcon, X, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { FILE_ACCEPT } from "./fieldTypes";

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export async function openFormFile(client, file) {
  return viewFromApi(client, `/forms/files/${file.id}`, { title: file.name || "Attachment", filename: file.name, type: file.mime });
}


export async function uploadFormFile(client, path, file) {
  if (file.size > 10 * 1024 * 1024) throw new Error("File is too large (max 10 MB).");
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await client.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
}

export function FileChip({ file, client, onRemove, testid }) {
  const isImg = (file.mime || "").startsWith("image/");
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white pl-2 pr-1 py-1 text-xs max-w-full" data-testid={testid}>
      {isImg ? <ImageIcon className="h-3.5 w-3.5 text-violet-600 shrink-0" /> : <FileText className="h-3.5 w-3.5 text-red-600 shrink-0" />}
      <button type="button" onClick={() => client && openFormFile(client, file)} className="truncate max-w-[180px] text-slate-800 hover:text-primary hover:underline text-left inline-flex items-center gap-1" title="Open">
        {file.name} <ExternalLink className="h-3 w-3 text-slate-400" />
      </button>
      {file.size ? <span className="text-slate-400">{fmtSize(file.size)}</span> : null}
      {onRemove && (
        <button type="button" onClick={onRemove} className="h-6 w-6 rounded-md inline-flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600" title="Remove">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

export function FileUploader({ client, uploadPath, files = [], onChange, max = 5, label = "Add PDF or photo", disabled, testid = "file-uploader" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    setBusy(true);
    const next = [...files];
    for (const f of list.slice(0, max - files.length)) {
      try {
        next.push(await uploadFormFile(client, uploadPath, f));
      } catch (err) {
        toast.error(err.response?.data?.detail || err.message || `Upload failed: ${f.name}`);
      }
    }
    onChange(next);
    setBusy(false);
  };

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {files.map((f, i) => <FileChip key={f.id} file={f} client={client} onRemove={disabled ? undefined : () => onChange(files.filter((_, j) => j !== i))} testid={`${testid}-file-${i}`} />)}
      </div>
      {!disabled && files.length < max && (
        <>
          <input ref={ref} type="file" accept={FILE_ACCEPT} multiple className="hidden" onChange={pick} data-testid={`${testid}-input`} />
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-btn`}
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 hover:border-slate-400 transition disabled:opacity-60">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />} {busy ? "Uploading…" : label}
          </button>
          <span className="ml-2 text-[10px] text-slate-400">PDF, JPG, PNG, HEIC · max 10 MB</span>
        </>
      )}
    </div>
  );
}
